import React from 'react';
import styled from 'styled-components';
import { motion, AnimatePresence } from 'framer-motion';
import { FaUndoAlt, FaDoorOpen } from 'react-icons/fa';

const ModalOverlay = styled(motion.div)`
  position: fixed;
  top: 0;
  left: 0;
  width: 100vw;
  height: 100vh;
  background: rgba(0, 0, 0, 0.75);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
`;

const ModalContent = styled(motion.div)`
  width: 300px;
  height: 160px;
  border-radius: 10px;
  box-shadow: 0px 5px 5px 0px rgba(0, 0, 0, 0.5);
  background: rgba(0, 32, 64, 1);
  display: flex;
  flex-direction: column;
`;

const ModalHeader = styled.div`
  width: 300px;
  height: 40px;
  background: rgba(0, 13, 26, 1);
  border-radius: 10px 10px 0 0;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const HeaderTitle = styled.h2`
  width: 280px;
  height: 20px;
  color: rgba(255, 255, 255, 1);
  font-family: 'Roboto Condensed', sans-serif;
  font-size: 20px;
  font-weight: 600;
  line-height: 23px;
  text-align: center;
  text-transform: uppercase;
  margin: 0;
`;

const ModalBody = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: space-between;
  padding: 16px 20px 20px;
`;

const BodyText = styled.p`
  width: 260px;
  height: 40px;
  color: rgba(255, 255, 255, 1);
  font-family: 'Roboto Condensed', sans-serif;
  font-size: 16px;
  font-weight: 400;
  line-height: 19px;
  text-align: center;
  margin: 0;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const ButtonRow = styled.div`
  display: flex;
  gap: 20px;
  justify-content: center;
`;

const ModalButton = styled.button<{ $variant: 'cancel' | 'confirm' }>`
  width: 120px;
  height: 40px;
  border-radius: 5px;
  box-shadow: 0px 0px 5px 0px rgba(0, 0, 0, 0.5);
  background: ${({ $variant }) =>
    $variant === 'cancel'
      ? 'linear-gradient(180deg, rgba(255, 64, 0, 1), rgba(64, 16, 0, 1) 100%)'
      : 'linear-gradient(180deg, rgba(0, 127, 255, 1), rgba(0, 32, 64, 1) 100%)'};
  color: rgba(255, 255, 255, 1);
  border: none;
  cursor: pointer;
  font-family: 'Fjalla One', sans-serif;
  font-size: 24px;
  font-weight: 400;
  line-height: 30px;
  text-align: center;
  text-transform: uppercase;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  transition: all 0.2s ease;

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 6px 20px rgba(0, 0, 0, 0.4);
  }

  &:active {
    transform: translateY(0);
  }
`;

interface ConfirmationModalProps {
  isOpen: boolean;
  title: string;
  message: string;
  onConfirm: () => void;
  onCancel: () => void;
  type?: 'undo' | 'quit';
}

const ConfirmationModal: React.FC<ConfirmationModalProps> = ({ 
  isOpen, 
  title, 
  message, 
  onConfirm, 
  onCancel, 
  type = 'undo' 
}) => {
  const getIcon = () => {
    switch (type) {
      case 'quit':
        return <FaDoorOpen size={20} />;
      case 'undo':
      default:
        return <FaUndoAlt size={20} />;
    }
  };

  const getConfirmText = () => {
    switch (type) {
      case 'quit':
        return 'QUIT';
      case 'undo':
      default:
        return 'UNDO';
    }
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <ModalOverlay
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.3 }}
        >
          <ModalContent
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.8 }}
            transition={{ duration: 0.4, ease: 'easeInOut' }}
          >
            <ModalHeader>
              <HeaderTitle>{title}</HeaderTitle>
            </ModalHeader>
            <ModalBody>
              <BodyText>{message}</BodyText>
              <ButtonRow>
                <ModalButton $variant="cancel" onClick={onCancel}>
                  CANCEL
                </ModalButton>
                <ModalButton $variant="confirm" onClick={onConfirm}>
                  {getIcon()}
                  {getConfirmText()}
                </ModalButton>
              </ButtonRow>
            </ModalBody>
          </ModalContent>
        </ModalOverlay>
      )}
    </AnimatePresence>
  );
};

export default ConfirmationModal;
